import { MarketType } from 'src/solace/constants/marketType.enum';

enum MARKET_RESPONSE_STATUS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  HALTED = 'HALTED',
  CLOSE_ONLY = 'CLOSE_ONLY',
}

export enum MARKET_STATUS_TYPE {
  active = 'active',
  inactive = 'inactive',
  halted = 'halted',
  closeOnly = 'closeOnly',
}

const marketSatusMapper: Record<MARKET_RESPONSE_STATUS, MARKET_STATUS_TYPE> = {
  [MARKET_RESPONSE_STATUS.ACTIVE]: MARKET_STATUS_TYPE.active,
  [MARKET_RESPONSE_STATUS.INACTIVE]: MARKET_STATUS_TYPE.inactive,
  [MARKET_RESPONSE_STATUS.HALTED]: MARKET_STATUS_TYPE.halted,
  [MARKET_RESPONSE_STATUS.CLOSE_ONLY]: MARKET_STATUS_TYPE.closeOnly,
} as any;

export interface IMarketResponse {
  open_price: number;
  max_price: number;
  min_price: number;
  weighted_average_price: number;
  variation_percentage: number;
  volume: number;
  volume_in_six_months: number;
  bid: number;
  bid_size: number;
  ask: number;
  ask_size: number;
  is_component: boolean;
  security_id: string;
  issuer: string;
  last_price: number;
  close_price: number;
  is_sic: boolean;
  instrument_id?: string;
  sector?: string;
  external_id?: string;
  status?: MARKET_RESPONSE_STATUS;
}

export interface IMarketDigitalResponse {
  openPrice: number;
  maxPrice: number;
  minPrice: number;
  ppp: number;
  percentageChange: number;
  aggregatedVolume: number;
  averageVolume6M: number;
  bidPrice: number;
  bidVolume: number;
  askPrice: number;
  askVolume: number;
  ipcParticipationRate: number;
  issueID: string;
  issueName: string;
  lastPrice: number;
  closePrice: number;
  instrumentType: number;
}

export interface IMarketSolaceResponse {
  OpenPrice: number;
  MaxPrice: number;
  MinPrice: number;
  BMVWeightedAvgPrice: number;
  PercentageChange: number;
  OperationVolume: number;
  averageVolume6M: number;
  bidPrice: number;
  bidVolume: number;
  askPrice: number;
  askVolume: number;
  ipcParticipationRate: number;
  StockSeries: string;
  issueName: string;
  Last: number;
  LastClose: number;
  instrumentType: number;
}

export interface IMarket {
  openPrice: number;
  maxPrice: number;
  minPrice: number;
  weightedAveragePrice: number;
  variationPercentage: number;
  volume: number;
  volumeInSixMonths: number;
  bid: number;
  bidSize: number;
  ask: number;
  askSize: number;
  ipcParticipationRate: number;
  securityId: string;
  issuer: string;
  lastPrice: number;
  closePrice: number;
  instrumentType: number;
  instrumentId?: string;
  sector?: string;
  externalId?: string;
  status?: MARKET_STATUS_TYPE;
}

export class MarketViewModel implements IMarket {
  static fromJSON(json: IMarketResponse): MarketViewModel {
    const instrumentType = json.is_sic ? MarketType.SIC : MarketType.BMV;
    const participationRate = 1;
    const noneParticipationRate = 0;
    const ipcParticipationRate = json.is_component
      ? participationRate
      : noneParticipationRate;
    return new MarketViewModel(
      json.open_price,
      json.max_price,
      json.min_price,
      json.weighted_average_price,
      json.variation_percentage,
      json.volume,
      json.volume_in_six_months,
      json.bid,
      json.bid_size,
      json.ask,
      json.ask_size,
      ipcParticipationRate,
      json.security_id,
      json.issuer,
      json.last_price,
      json.close_price,
      instrumentType,
      json.instrument_id,
      json.sector,
      json.external_id,
      (marketSatusMapper as any)[json.status],
    );
  }

  static fromDigitalJSON(json: IMarketDigitalResponse): MarketViewModel {
    return new MarketViewModel(
      json.openPrice,
      json.maxPrice,
      json.minPrice,
      json.ppp,
      json.percentageChange,
      json.aggregatedVolume,
      json.averageVolume6M,
      json.bidPrice,
      json.bidVolume,
      json.askPrice,
      json.askVolume,
      json.ipcParticipationRate,
      json.issueID,
      json.issueName,
      json.lastPrice,
      json.closePrice,
      json.instrumentType,
    );
  }

  static fromWebsocketJSON(json: IMarketSolaceResponse): MarketViewModel {
    return new MarketViewModel(
      json.OpenPrice,
      json.MaxPrice,
      json.MinPrice,
      json.BMVWeightedAvgPrice,
      json.PercentageChange,
      json.OperationVolume,
      json.averageVolume6M,
      json.bidPrice,
      json.bidVolume,
      json.askPrice,
      json.askVolume,
      json.ipcParticipationRate,
      json.StockSeries,
      json.issueName,
      json.Last,
      json.LastClose,
      json.instrumentType,
    );
  }

  constructor(
    public openPrice: number,
    public maxPrice: number,
    public minPrice: number,
    public weightedAveragePrice: number,
    public variationPercentage: number,
    public volume: number,
    public volumeInSixMonths: number,
    public bid: number,
    public bidSize: number,
    public ask: number,
    public askSize: number,
    public ipcParticipationRate: number,
    public securityId: string,
    public issuer: string,
    public lastPrice: number,
    public closePrice: number,
    public instrumentType: number,
    public instrumentId?: string,
    public sector?: string,
    public externalId?: string,
    public status?: MARKET_STATUS_TYPE,
  ) {}
}
